import { useCallback, useEffect, useRef, useState } from "react";
import { UploadCloud, X, Image as ImageIcon, Film } from "lucide-react";
import { toast } from "sonner";

const MAX_FILES = 10;
const MAX_FILE_BYTES = 50 * 1024 * 1024; // 50MB

/* Detect "portrait" vs "landscape" from an image or video file. */
const detectOrientation = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    if (file.type.startsWith("image/")) {
      const img = new Image();
      img.onload = () => {
        const o =
          img.naturalHeight >= img.naturalWidth ? "portrait" : "landscape";
        URL.revokeObjectURL(url);
        resolve({ orientation: o, previewUrl: URL.createObjectURL(file) });
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("Couldn't read image"));
      };
      img.src = url;
      return;
    }
    if (file.type.startsWith("video/")) {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.muted = true;
      video.onloadedmetadata = () => {
        const o =
          video.videoHeight >= video.videoWidth ? "portrait" : "landscape";
        URL.revokeObjectURL(url);
        resolve({ orientation: o, previewUrl: URL.createObjectURL(file) });
      };
      video.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("Couldn't read video metadata"));
      };
      video.src = url;
      return;
    }
    URL.revokeObjectURL(url);
    reject(new Error("Unsupported file type"));
  });

const MediaDropzone = ({ files, onChange, orientation, onOrientationChange }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  // Revoke any preview URLs when component unmounts
  useEffect(() => {
    return () => {
      files.forEach((f) => {
        if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
      });
    };
    // Mount-only cleanup — object URLs are revoked per-change elsewhere.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addFiles = useCallback(
    async (incoming) => {
      const list = Array.from(incoming || []);
      if (!list.length) return;

      if (files.length + list.length > MAX_FILES) {
        toast(`You can attach up to ${MAX_FILES} files`, { duration: 2800 });
        return;
      }

      let lockedOrientation = orientation;
      const accepted = [];

      for (const file of list) {
        if (file.size > MAX_FILE_BYTES) {
          toast(`${file.name} is over 50MB`, { duration: 2800 });
          continue;
        }
        if (
          !file.type.startsWith("image/") &&
          !file.type.startsWith("video/")
        ) {
          toast(`${file.name} is not an image or video`, { duration: 2800 });
          continue;
        }
        try {
          const { orientation: o, previewUrl } = await detectOrientation(file);
          if (!lockedOrientation) {
            lockedOrientation = o;
          } else if (o !== lockedOrientation) {
            toast(
              `${file.name} is ${o}. Please upload only ${lockedOrientation} files.`,
              { duration: 3200 }
            );
            URL.revokeObjectURL(previewUrl);
            continue;
          }
          accepted.push({
            id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()
              .toString(16)
              .slice(2, 8)}`,
            file,
            kind: file.type.startsWith("video/") ? "video" : "image",
            previewUrl,
            orientation: o,
            size: file.size,
            name: file.name,
          });
        } catch (err) {
          toast(`Couldn't read ${file.name}`, { duration: 2800 });
        }
      }

      if (accepted.length) {
        if (!orientation && lockedOrientation) {
          onOrientationChange(lockedOrientation);
        }
        onChange([...files, ...accepted]);
      }
    },
    [files, orientation, onChange, onOrientationChange]
  );

  const removeFile = (id) => {
    const next = files.filter((f) => f.id !== id);
    const removed = files.find((f) => f.id === id);
    if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
    onChange(next);
    if (next.length === 0) onOrientationChange(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer?.files);
  };

  const handleClick = () => {
    if (files.length >= MAX_FILES) {
      toast(`You can attach up to ${MAX_FILES} files`, { duration: 2400 });
      return;
    }
    inputRef.current?.click();
  };

  const aspectClass =
    orientation === "portrait" ? "aspect-[3/4]" : "aspect-[4/3]";

  return (
    <div data-testid="media-dropzone" className="space-y-4">
      <div
        role="button"
        tabIndex={0}
        data-testid="media-dropzone-area"
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={
          "relative flex flex-col items-center justify-center gap-3 border-2 border-dashed px-6 py-10 cursor-pointer transition-colors text-center " +
          (dragging
            ? "border-[var(--ld-gold-deep)] bg-[var(--ld-gold)]/5"
            : "border-[var(--ld-ink)]/30 hover:border-[var(--ld-gold-deep)]/70")
        }
      >
        <UploadCloud
          size={28}
          strokeWidth={1.4}
          className="text-[var(--ld-gold-deep)]"
        />
        <p className="text-sm text-[var(--ld-ink)]">
          <span className="font-medium">Drag &amp; drop</span> images or videos
          here, or{" "}
          <span className="underline underline-offset-4 text-[var(--ld-gold-deep)]">
            click to browse
          </span>
        </p>
        <p className="text-[11px] tracking-[0.15em] uppercase text-[var(--ld-ink-muted)]">
          Up to {MAX_FILES} files · 50MB each · JPG / PNG / WEBP / MP4 / MOV
        </p>
        {orientation && (
          <p
            data-testid="media-orientation-lock"
            className="text-[11px] tracking-[0.15em] uppercase text-[var(--ld-gold-deep)]"
          >
            Layout locked · {orientation}
          </p>
        )}
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*,video/*"
          className="sr-only"
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = "";
          }}
          data-testid="media-file-input"
        />
      </div>

      {files.length > 0 && (
        <div
          data-testid="media-thumbs"
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3"
        >
          {files.map((f) => (
            <div
              key={f.id}
              data-testid={`media-thumb-${f.id}`}
              className={`relative ${aspectClass} overflow-hidden border border-[var(--ld-ink)]/15 bg-[var(--ld-ink)]/5 group`}
            >
              {f.kind === "image" ? (
                <img
                  src={f.previewUrl}
                  alt={f.name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <>
                  <video
                    src={f.previewUrl}
                    muted
                    playsInline
                    preload="metadata"
                    className="h-full w-full object-cover"
                  />
                  <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] tracking-[0.18em] uppercase bg-[var(--ld-ink)]/80 text-[var(--ld-gold)]">
                    <Film size={10} strokeWidth={1.6} />
                    Video
                  </span>
                </>
              )}
              <button
                type="button"
                data-testid={`media-remove-${f.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(f.id);
                }}
                aria-label={`Remove ${f.name}`}
                className="absolute top-2 right-2 inline-flex h-7 w-7 items-center justify-center bg-[var(--ld-ink)]/80 text-[var(--ld-gold)] opacity-0 group-hover:opacity-100 transition-opacity focus:opacity-100"
              >
                <X size={13} strokeWidth={1.8} />
              </button>
              <div className="absolute bottom-0 inset-x-0 px-2 py-1 text-[10px] tracking-[0.1em] uppercase bg-[var(--ld-ink)]/70 text-[var(--ld-gold)] truncate">
                {f.kind === "image" ? (
                  <ImageIcon
                    size={10}
                    strokeWidth={1.6}
                    className="inline mr-1"
                  />
                ) : null}
                {f.name}
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-[11px] text-[var(--ld-ink-muted)] leading-relaxed">
        All files must share the same layout (portrait or landscape) — this
        keeps your profile gallery looking unified. The orientation locks to
        whatever you upload first; clear all files to switch.
      </p>
    </div>
  );
};

export default MediaDropzone;
